/**
 * The bid's Confirm button (DESIGN-client §4).
 *
 * It is the shell's own gold primary — the same `Button` component "Start the game" uses — sitting
 * on the felt at the anchor the scene publishes for it. For one release it was drawn in Pixi, and a
 * playtester's verdict was "you have a bunch of nice buttons and you produce this tragedy": a
 * hand-tessellated gradient with hand-estimated text metrics will never match a button the design
 * system already has, and at a phone's scale it came out a pale squashed slab.
 *
 * The split is the same one `BlindNilPrompt` uses. The scene decides whether there is a bid to
 * confirm and what it is (`presentation.bidConfirm`, written by `BidLayer` through `setBidConfirm`)
 * and where it goes (`presentation.anchors['bid.commit']`, which is `bid.confirmY` — the row
 * `solveBid` reserves under the slider and above the card fan). The shell decides what it looks
 * like. The commit rides along IN the offer: it lived in a module handle in `shell/runtime` for one
 * release, registered at the end of `TableScreen`'s async scene build and cleared by its disposer,
 * and under React's double-mount the disposer nulled it while the button was still on screen — so
 * clicking did nothing and only Enter worked, that being the one route that did not go through it.
 * One object decides that there is a bid and how to send it, so the two cannot come apart.
 *
 * Keyboard: it is a real `<button>`, so it is in the tab order and Enter/Space activate it. The
 * scene keeps its own held-Enter slam for the players who learned it.
 */
import { useStore } from '../../store/store';
import { Button } from '../ui';
import { resolveAnchor } from './CoachMark';

/** §4 copy. Built from the value, so the button cannot promise a bid other than the one it sends. */
export function confirmLabel(offer: { value: number; nil: boolean }): string {
  return offer.nil ? 'Bid NIL' : `Bid ${offer.value}`;
}

export function BidConfirm() {
  const offer = useStore((s) => s.presentation.bidConfirm);
  const anchors = useStore((s) => s.presentation.anchors);
  const rect = resolveAnchor(anchors, 'bid.commit');

  // No offer is the common case — every phase that is not a live bid of mine. No anchor means the
  // scene has not solved a layout yet, and a button in the top-left corner is worse than none.
  if (!offer || !rect) return null;

  return (
    <div
      className="bid-confirm"
      style={{ left: rect.x + rect.width / 2, top: rect.y + rect.height / 2 }}
      data-testid="bid-confirm-slot"
    >
      <Button variant="primary" size="lg" testId="bid-confirm" onClick={() => offer.commit()}>
        {confirmLabel(offer)}
      </Button>
    </div>
  );
}
